import {Address} from './client';
import {FeePlan} from './fee';
import {LegalRepresentative} from './person';
import {RecipientBankAccount} from './recipient-bank-account';
import {RecipientOfficialData} from './recipient';
import {InternalNote} from './system';

export const averageTicketValues = [
  {
    value: '0-25',
    name: 'Até R$25,00',
  },
  {
    value: '26-50',
    name: 'De R$26,00 a R$50,00',
  },
  {
    value: '51-100',
    name: 'De R$51,00 a R$100,00',
  },
  {
    value: '101-300',
    name: 'De R$101,00 a R$300,00',
  },
  {
    value: '301-500',
    name: 'De R$301,00 a R$500,00',
  },
  {
    value: '501-1000',
    name: 'De R$501,00 a R$1000,00',
  },
  {
    value: '1000+',
    name: '+ de R$1000,00',
  },
];

export const monthlyRevenueExpectationValues = [
  {
    value: '0-15000',
    name: 'Até R$15.000',
  },
  {
    value: '15000-50000',
    name: 'De R$15.000 até R$50.000',
  },
  {
    value: '50000-100000',
    name: 'De R$50.000 até R$100.000',
  },
  {
    value: '100000-300000',
    name: 'De R$100.000 até R$300.000',
  },
  {
    value: '300000-500000',
    name: 'De R$300.000 até R$500.000',
  },
  {
    value: '500000-1000000',
    name: 'De R$500.000 até R$1.000.000',
  },
  {
    value: '1000000+',
    name: '+ de 1 milhão',
  },
];

export const companyTypes = [
  {label: 'SA', value: 'SA'},
  {label: 'LTDA', value: 'LTDA'},
  {label: 'MEI', value: 'MEI'},
  {label: 'ME', value: 'ME'},
  {label: 'EIRELI', value: 'EIRELI'},
];

export const operatingRegions = [
  'ASIA',
  'LATAM',
  'BRAZIL',
  'EUROPE',
  'OCEANIA',
  'MERCOSUR',
  'NORTH_AMERICA',
];

export const organizationalTools = [
  'ERP_SYSTEM',
  'SAP_SYSTEM',
  'RISK_POLICY',
  'INTERNAL_AUDIT',
  'PRIVACY_AND_DATA_HANDLING_POLICY',
  'ANTI_MONEY_LAUNDERING_ETHICS_POLICY',
  'NO_ORGANIZATION_TOOL',
];

export const clientTypes = [
  'BUSINESS',
  'INDIVIDUAL',
  'BUSINESS_AND_INDIVIDUAL',
];

export const clientTypesOptions = [
  {value: 'BUSINESS', label: 'Pessoas Jurídicas'},
  {value: 'INDIVIDUAL', label: 'Pessoas Físicas'},
  {value: 'BUSINESS_AND_INDIVIDUAL', label: 'Ambos'},
];

/** How the onboarding was created (entry source). */
export type OnboardingOrigin = 'lead_driven' | 'self_service';

/** Checklist item type id (from GET checklist-items). */
export type ComplianceChecklistItemTypeId =
  | 'internal_overall_review'
  | 'documentation_legal_status'
  | 'partners_background_check'
  | 'business_check';

/** One item in the compliance checklist on an onboarding. */
export interface ComplianceChecklistItem {
  id: ComplianceChecklistItemTypeId | string;
  /** Check was performed (reviewed). */
  done: boolean;
  /** When done is true: true = passed, false = rejected. When done is false, irrelevant. */
  approved?: boolean;
  doneAt?: string;
  description?: string;
}

export interface ComplianceChecklist {
  items: ComplianceChecklistItem[];
}

/** Default checklist item option from GET checklist-items. */
export interface ChecklistItemOption {
  id: ComplianceChecklistItemTypeId;
  label: string;
}

/** Payload to update compliance checklist. */
export interface ComplianceChecklistItemPayload {
  id: ComplianceChecklistItemTypeId | string;
  done: boolean;
  approved?: boolean;
  doneAt?: string;
  description?: string;
}

export interface UpdateComplianceChecklistPayload {
  items: ComplianceChecklistItemPayload[];
}

/** Payload for PATCH update-status. kycUrl required when status === 'pending_kyc'. */
export interface UpdateOnboardingStatusPayload {
  status: string;
  reason: string;
  kycUrl?: string;
}

export interface Onboarding {
  id: number;
  name: string;
  displayName: string;
  identifier?: string;
  foundationDate: string;
  document: string;
  website: string;
  status: string;
  origin?: OnboardingOrigin;
  address?: Address;
  representative: LegalRepresentative;
  metadata?: OnboardingMetadata;
  kycUrl?: string;
  complianceChecklist?: ComplianceChecklist;
  files: OnboardingFileUpload[];
  partners: LegalRepresentative[];
  officialData?: RecipientOfficialData;
  internalNotes?: InternalNote[];
  onboardingMetadata?: OnboardingMetadata;
  feePlan?: Pick<FeePlan, 'id' | 'name'> | null;
  createdAt: Date;
  updatedAt?: Date;
}
export interface OnboardingMetadata {
  estimatedMonthlyVolume: string;
  estimatedAverageTicket: string;
  businessAreas: string[];
  clientType: string;
  organizationalTools: string[];
  operatingRegions: string[];
  companyType: string;
  accountCreated?: boolean;
  recipientCreated?: boolean;
  providerRequest: {
    accountEmail?: string;
  };
  bankAccount?: Partial<RecipientBankAccount>;
}

export interface OnboardingFileUpload {
  type: string;
  base64: string;
  checked?: boolean;
}

export enum PartnerRole {
  PARTNER = 'partner',
  MANAGNIG_PARTNER = 'managing_partner',
  LEGAL_REPRESENTATIVE = 'legal_representative',
}

export const partnerRoleOptions = [
  {label: 'Sócio', value: PartnerRole.PARTNER},
  {label: 'Sócio Administrador', value: PartnerRole.MANAGNIG_PARTNER},
  {label: 'Representante Legal', value: PartnerRole.LEGAL_REPRESENTATIVE},
];

export enum OnboardingStatus {
  DRAFT = 'draft',
  INTERNAL_REVIEW = 'internal_review',
  PENDING = 'pending',
  SENT = 'sent',
  PENDING_KYC = 'pending_kyc',
  PENDING_ACCOUNT_CREATION = 'pending_account_creation',
  IN_PROGRESS = 'in_progress',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  FINISHED = 'finished',
  FAILED = 'failed',
}
